/**
 * The lock screen — sign-in styled after the macOS lock screen: big clock and
 * date over the wallpaper, a centered user avatar, one pill field at a time.
 *
 * Two steps, like macOS: enter the email and advance (the arrow, or Enter),
 * then the password field appears and Enter signs in. The email step asks the
 * engine whether the address is a staff account (`engine.staffExists`), so an
 * address that isn't staff is caught with "We don't recognize that email."
 * before a password is ever asked for. Sign-in is the engine's own
 * (`engine.signIn`); staff roles only (a learner account signs in at the engine
 * but is refused here).
 */
import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "../auth";

function useClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 15_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/** A neutral macOS-style user glyph — no branding, works for any account. */
function PersonGlyph() {
  return (
    <svg viewBox="0 0 44 44" width="42" height="42" fill="currentColor" aria-hidden="true">
      <circle cx="22" cy="16" r="8" />
      <path d="M8 38c0-7.7 6.3-13 14-13s14 5.3 14 13a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2Z" />
    </svg>
  );
}

export function LockScreen({ notEditor }: { notEditor?: boolean }) {
  const { checkEmail, signIn, signOut } = useAuth();
  const now = useClock();
  const [step, setStep] = useState<"email" | "password">("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitEmail = async () => {
    const value = email.trim();
    if (busy || !value) return;
    setBusy(true);
    setError(null);
    try {
      if (await checkEmail(value)) {
        setStep("password");
      } else {
        setError("We don't recognize that email.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async () => {
    if (busy || !password) return;
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
    } catch {
      setError("That password isn't right.");
      setPassword("");
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void (step === "email" ? submitEmail() : submitPassword());
  };

  const backToEmail = () => {
    setStep("email");
    setPassword("");
    setError(null);
  };

  const time = now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const date = now.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="rdr-lock">
      <div className="rdr-lock-date">{date}</div>
      <div className="rdr-lock-clock">{time}</div>

      <div className="rdr-lock-panel">
        <span className="rdr-lock-avatar" aria-hidden="true">
          <PersonGlyph />
        </span>

        {notEditor ? (
          <>
            <span className="rdr-lock-name">No engine access on this account.</span>
            <span className="rdr-lock-hint">An admin grants it in the Staff app.</span>
            <button
              type="button"
              className="rdr-lock-signout cursor-pointer"
              onClick={() => void signOut()}
            >
              Sign out
            </button>
          </>
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col items-center gap-2.5">
            {step === "email" ? (
              <div className="rdr-lock-fieldwrap">
                <input
                  className="rdr-lock-field"
                  type="email"
                  placeholder="Email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                />
                <button
                  type="submit"
                  className="rdr-lock-go cursor-pointer"
                  aria-label={busy ? "Checking" : "Continue"}
                  disabled={busy || !email.trim()}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
                    <path
                      d="M5 12h13m0 0-5-5m5 5-5 5"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            ) : (
              <>
                <span className="rdr-lock-name">{email.trim()}</span>
                {/* Hidden username so password managers associate the credential
                    with this account (the email lives on the prior step). */}
                <input type="text" name="username" autoComplete="username" value={email.trim()} readOnly hidden />
                <div className="rdr-lock-fieldwrap">
                  <input
                    className="rdr-lock-field"
                    type="password"
                    placeholder="Password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="rdr-lock-go cursor-pointer"
                    aria-label={busy ? "Signing in" : "Sign in"}
                    disabled={busy || !password}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
                      <path
                        d="M5 12h13m0 0-5-5m5 5-5 5"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </>
            )}

            {error ? (
              <span className="rdr-lock-error">{error}</span>
            ) : (
              <span className="rdr-lock-hint">
                {busy
                  ? step === "email"
                    ? "Checking…"
                    : "Signing in…"
                  : step === "email"
                    ? "Enter your email to continue"
                    : "Press Enter to sign in"}
              </span>
            )}

            {step === "password" && !busy ? (
              <button type="button" className="rdr-lock-textlink cursor-pointer" onClick={backToEmail}>
                Use a different email
              </button>
            ) : null}
          </form>
        )}
      </div>
    </div>
  );
}
